import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { pricingTabs } from "../../mock";
import { Check, Star, Sparkles } from "lucide-react";
import { useCurrency } from "../../context/CurrencyContext";
import { API } from "../../lib/api";
import CurrencySwitcher from "../CurrencySwitcher";

/**
 * PricingTeaser
 *   - Occasion tabs (Wedding / Engagement / Party / Business / Memorial / Kids)
 *     are now stateful and swap the tagline / suggested-uses line, not the
 *     underlying tier package (packages are identical across occasions —
 *     that's the pricing model).
 *   - Tier cards read live from /api/payments/tiers so currency + upload caps
 *     stay authoritative from the backend.
 *   - Free tier is shown as a slim "Start free" card above the paid trio.
 *     Backend TIERS['free'] is the source of truth (currently 100 uploads / 7 days).
 */

const paidPlanMeta = [
  { key: "petite", name: "Petite", tag: "Intimate gatherings", popular: false },
  { key: "classic", name: "Classic", tag: "For your special day", popular: true },
  { key: "grand", name: "Grand", tag: "Large celebrations", popular: false },
];

// Kids Party gets a dedicated single-card layout instead of the paid trio.
// It's a smaller, cheaper offering and clogging it into the 3-tier grid
// dilutes both the standard trio and the kids messaging.
const kidsPlanMeta = { key: "kids", name: "Kids Party", tag: "Cake, chaos, capes", popular: true };

// Per-occasion tagline + suggested audience — the ONLY thing that changes
// when the user flips tabs. Keep short and evocative.
const OCCASION_COPY = {
  wedding: { hero: "Weddings", sub: "For your special day — from ceremony to send-off." },
  engagement: { hero: "Engagements", sub: "Ring shots, tears, and every stolen glance." },
  party: { hero: "Parties", sub: "Birthdays, reunions, and everything in between." },
  business: { hero: "Business events", sub: "Launches, conferences, team offsites." },
  memorial: { hero: "Memorials", sub: "A gentle, private place to remember together." },
  kids: { hero: "Kids' parties", sub: "Cake smashes, capes, and chaos — all in one place." },
};

// Map incoming URL path -> tab id, so /engagements-pricing lands on Engagement.
const PATH_TO_TAB = {
  "/weddings-pricing": "wedding",
  "/engagements-pricing": "engagement",
  "/party-pricing": "party",
  "/business-pricing": "business",
  "/memorials-pricing": "memorial",
  "/kids-party-pricing": "kids",
};

const PricingTeaser = () => {
  const location = useLocation();
  const initial = useMemo(() => PATH_TO_TAB[location.pathname] || "wedding", [location.pathname]);
  const [active, setActive] = useState(initial);
  useEffect(() => { setActive(initial); }, [initial]);

  const { currency, format } = useCurrency();
  const [tiers, setTiers] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payments/tiers?currency=${currency}`)
      .then((r) => setTiers(r.data))
      .catch(() => {});
  }, [currency]);

  const copy = OCCASION_COPY[active] || OCCASION_COPY.wedding;
  const free = tiers?.free;

  return (
    <section className="section bg-cream-200/60" data-testid="pricing-teaser">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="text-center mb-10">
          <h2 className="font-serif text-navy-700 text-4xl md:text-5xl leading-tight">
            Pricing <span className="italic">tailored to your event type</span>
          </h2>
          <p className="mt-4 text-navy-700/70 max-w-2xl mx-auto">
            Packages and inclusions are matched to your occasion — select yours to explore.
          </p>
          <div className="mt-6 inline-flex items-center gap-3 bg-white rounded-full px-4 py-2 soft-shadow">
            <span className="text-xs uppercase tracking-wider text-navy-700/60 font-semibold">Currency</span>
            <CurrencySwitcher compact />
          </div>
        </div>

        {/* Occasion tabs — stateful, DO update the tagline below */}
        <div className="flex flex-wrap justify-center gap-3 mb-6" data-testid="pricing-occasion-tabs">
          {pricingTabs.map((t) => {
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                data-testid={`occasion-tab-${t.id}`}
                aria-pressed={isActive}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all border ${
                  isActive
                    ? "bg-coral-300 text-white border-coral-300 shadow"
                    : "bg-white text-navy-700 border-cream-300 hover:bg-coral-50 hover:border-coral-200"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Occasion tagline — updates on tab click */}
        <div className="text-center mb-10">
          <p className="text-navy-700/70" data-testid="pricing-occasion-tagline">
            <span className="text-coral-500 font-semibold">{copy.hero}:</span> {copy.sub}
          </p>
        </div>

        {/* Free tier — slim banner card above the paid trio. Hidden on
            Kids Party tab (kids gets a single dedicated card, no free row). */}
        {free && active !== "kids" && (
          <div
            data-testid="pricing-free-card"
            className="max-w-5xl mx-auto mb-6 flex flex-wrap items-center justify-between gap-4 bg-white rounded-2xl border border-cream-300/60 soft-shadow p-5 md:p-6"
          >
            <div className="flex items-start gap-3 flex-1 min-w-[240px]">
              <div className="w-10 h-10 rounded-full bg-coral-100 text-coral-500 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-serif italic text-xl text-navy-700">Free Demo</h3>
                <p className="text-sm text-navy-700/60 mt-0.5">
                  Try GalerieCop with no commitment. {free.max_uploads} uploads · {free.days_active} days hosting · 1 QR code.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-3xl font-serif text-navy-700">{format(0)}</div>
                <div className="text-xs text-navy-700/50">no card required</div>
              </div>
              <Link
                to="/get-started"
                data-testid="pricing-free-cta"
                className="bg-navy-700 hover:bg-navy-800 text-white font-semibold px-5 py-3 rounded-full text-sm whitespace-nowrap"
              >
                Start free
              </Link>
            </div>
          </div>
        )}

        {/* Tier cards — Kids Party gets a single centered card; every other
            occasion shows the standard Petite/Classic/Grand trio. */}
        {active === "kids" ? (
          <div className="max-w-md mx-auto" data-testid="pricing-kids-panel">
            {(() => {
              const t = tiers?.kids;
              return (
                <div
                  data-testid="pricing-card-kids"
                  className="relative rounded-2xl p-8 bg-white border-2 border-coral-300 soft-shadow-coral text-center"
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-coral-300 text-white text-[11px] font-bold tracking-wider uppercase px-3 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" /> Just for Kids
                  </div>
                  <h3 className="font-serif italic text-3xl text-navy-700">{kidsPlanMeta.name}</h3>
                  <p className="text-sm text-navy-700/60 mt-1">{kidsPlanMeta.tag}</p>
                  <div className="mt-6 flex items-baseline gap-2 justify-center">
                    <span className="text-6xl font-serif text-navy-700">
                      {t ? format(t.price) : "..."}
                    </span>
                    <span className="text-sm text-navy-700/60">one-time</span>
                  </div>
                  <ul className="mt-6 space-y-3 text-left">
                    {(t?.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-navy-700/80">
                        <Check className="w-4 h-4 text-coral-300 mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/get-started"
                    data-testid="pricing-cta-kids"
                    className="mt-7 block text-center py-3 rounded-full font-semibold bg-coral-300 text-white hover:bg-coral-400"
                  >
                    Choose Kids Party
                  </Link>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {paidPlanMeta.map((p) => {
              const t = tiers?.[p.key];
              return (
                <div
                  key={p.name}
                  data-testid={`pricing-card-${p.key}`}
                  className={`relative rounded-2xl p-7 bg-white border ${p.popular ? "border-coral-300 soft-shadow-coral" : "border-cream-300/60 soft-shadow"}`}
                >
                  {p.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-coral-300 text-white text-[11px] font-bold tracking-wider uppercase px-3 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3 fill-white" /> Most Popular
                    </div>
                  )}
                  <h3 className="font-serif italic text-2xl text-navy-700">{p.name}</h3>
                  <p className="text-sm text-navy-700/60 mt-1">{p.tag}</p>
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="text-5xl font-serif text-navy-700">
                      {t ? format(t.price) : "..."}
                    </span>
                    <span className="text-sm text-navy-700/60">one-time</span>
                  </div>
                  <ul className="mt-6 space-y-3">
                    {(t?.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-navy-700/80">
                        <Check className="w-4 h-4 text-coral-300 mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/get-started"
                    data-testid={`pricing-cta-${p.key}`}
                    className={`mt-7 block text-center py-3 rounded-full font-semibold transition-colors ${p.popular ? "bg-coral-300 text-white hover:bg-coral-400" : "border-2 border-navy-700 text-navy-700 hover:bg-navy-700 hover:text-white"}`}
                  >
                    Choose {p.name}
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default PricingTeaser;
